import React from 'react';
import { Loader, Pagination } from '@mantine/core';
import {
  ONTOPO_METHOD_LABELS,
  ONTOPO_STATUS_LABELS,
  resolveStatusClass,
} from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type OntopoCallLogsTableProps = Pick<
  UseMonitoringPage,
  | 'ontopoRows'
  | 'isOntopoLoading'
  | 'hasLoadedOntopo'
  | 'ontopoTotalPages'
  | 'ontopoPage'
  | 'setOntopoPage'
  | 'openOntopoDetail'
>;

const statusBadgeClass = (status: string) => {
  if (status === 'success') return classes.levelBadgeMuted;
  if (status === 'timeout') return classes.levelBadgeWarn;
  return classes.levelBadgeError;
};

const httpBadgeClass = (statusClass: '4xx' | '5xx' | null) =>
  statusClass === '5xx' ? classes.statusBadge5xx : statusClass === '4xx' ? classes.statusBadge4xx : classes.levelBadgeMuted;

export const OntopoCallLogsTable: React.FC<OntopoCallLogsTableProps> = ({
  ontopoRows,
  isOntopoLoading,
  hasLoadedOntopo,
  ontopoTotalPages,
  ontopoPage,
  setOntopoPage,
  openOntopoDetail,
}) => {
  if (isOntopoLoading && !hasLoadedOntopo) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table} data-testid="monitoring-ontopo-table">
        <thead>
          <tr>
            <th>{'תאריך ושעה'}</th>
            <th>{'עסק'}</th>
            <th>{'פעילות'}</th>
            <th>{'סטטוס'}</th>
            <th>{'קוד HTTP'}</th>
            <th>{'זמן תגובה'}</th>
            <th>{'הזמנה'}</th>
            <th>{'שגיאה'}</th>
          </tr>
        </thead>
        <tbody>
          {ontopoRows.map((row) => (
            <tr
              key={row.id}
              className={classes.clickableRow}
              role="button"
              tabIndex={0}
              data-testid={`monitoring-ontopo-row-${row.id}`}
              onClick={() => openOntopoDetail(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openOntopoDetail(row);
                }
              }}
            >
              <td data-label={'תאריך ושעה'}>{formatDateTime(row.createdAt)}</td>
              <td data-label={'עסק'}>{row.companyName ?? row.tenantId ?? '-'}</td>
              <td data-label={'פעילות'}>{ONTOPO_METHOD_LABELS[row.method] ?? row.method}</td>
              <td data-label={'סטטוס'}>
                <span className={`${classes.detailKindBadge} ${statusBadgeClass(row.status)}`}>
                  {ONTOPO_STATUS_LABELS[row.status] ?? row.status}
                </span>
              </td>
              <td data-label={'קוד HTTP'}>
                {row.httpStatus != null ? (
                  <span className={`${classes.detailKindBadge} ${httpBadgeClass(resolveStatusClass(row.httpStatus))}`}>
                    {row.httpStatus}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td data-label={'זמן תגובה'}>{row.latencyMs != null ? `${row.latencyMs} ms` : '-'}</td>
              <td data-label={'הזמנה'}>{row.orderReference ?? (row.orderId != null ? String(row.orderId) : '-')}</td>
              <td data-label={'שגיאה'}>
                <div className={classes.errorMessageCell}>{row.errorMessage ?? '-'}</div>
              </td>
            </tr>
          ))}
          {ontopoRows.length === 0 && (
            <tr>
              <td colSpan={8} className={classes.empty}>
                {'אין רשומות מעקב אונטופו לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {ontopoTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={ontopoTotalPages} value={ontopoPage} onChange={setOntopoPage} size="sm" />
        </div>
      )}
    </div>
  );
};
